/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import type { AuditLogEventLocation } from '@atlassian/audit-logs-common';

import { DetailsRow } from '../../../../../common/utils/components/details-row';
import { DetailsSection } from '../../../../../common/utils/components/details-section';

const messages = defineMessages({
	location: {
		id: 'organization.audit.log.side-panel.tab-panels.details.location-section.title',
		defaultMessage: 'Location',
		description: 'Title for the location section in audit log details panel',
	},
	city: {
		id: 'organization.audit.log.side-panel.tab-panels.details.location-section.city.label',
		defaultMessage: 'City',
		description: 'Label for the city field in audit log location section',
	},
	ipAddress: {
		id: 'organization.audit.log.side-panel.tab-panels.details.location-section.ip-address.label',
		defaultMessage: 'IP address',
		description: 'Label for the IP address field in audit log location section',
	},
	region: {
		id: 'organization.audit.log.side-panel.tab-panels.details.location-section.region.label',
		defaultMessage: 'Region',
		description: 'Label for the region field in audit log location section',
	},
	country: {
		id: 'organization.audit.log.side-panel.tab-panels.details.location-section.country.label',
		defaultMessage: 'Country',
		description: 'Label for the country field in audit log location section',
	},
});

interface LocationSectionProps {
	location?: AuditLogEventLocation;
}

export const LocationSection = ({ location }: LocationSectionProps): JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!location) {
		return null;
	}

	const { city, ip, regionName, countryName } = location;

	if (!city && !ip && !regionName && !countryName) {
		return null;
	}

	return (
		<DetailsSection title={formatMessage(messages.location)}>
			{ip && <DetailsRow label={formatMessage(messages.ipAddress)} value={ip} />}
			{city && <DetailsRow label={formatMessage(messages.city)} value={city} />}
			{regionName && <DetailsRow label={formatMessage(messages.region)} value={regionName} />}
			{countryName && <DetailsRow label={formatMessage(messages.country)} value={countryName} />}
		</DetailsSection>
	);
};
